import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const CreditNoteApprovalCard = ({ creditNoteId, onApproved, onRejected }) => {
  // Data state
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Action state
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionMessageType, setActionMessageType] = useState(''); // 'success' or 'error'

  // Fetch credit note on mount
  useEffect(() => {
    const fetchCreditNote = async () => {
      try {
        setError('');
        const token = localStorage.getItem('authToken');

        const response = await axios.get(`/api/credit-notes/${creditNoteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = response.data.data || response.data;
        setCreditNote(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching credit note:', err);
        setError(
          err.response?.data?.message || err.message || 'Failed to load credit note'
        );
        setLoading(false);
      }
    };

    if (creditNoteId) {
      fetchCreditNote();
    }
  }, [creditNoteId]);

  // Determine if any line has VAT
  const hasVat = useMemo(() => {
    return creditNote?.lines?.some((line) => Number(line.vatAmount || 0) > 0) || false;
  }, [creditNote]);

  // Convert reason enum to label
  const getReasonLabel = (reason) => {
    const reasonMap = {
      qty_shortage: 'Quantity Shortage',
      price_discrepancy: 'Price Discrepancy',
      both: 'Qty & Price Mismatch',
      damaged_goods: 'Damaged Goods'
    };
    return reasonMap[reason] || reason;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    return statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

  // Handle approve
  const handleApprove = async () => {
    try {
      setActionMessage('');
      setApproving(true);

      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `/api/credit-notes/${creditNoteId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data.data || response.data;
      setCreditNote(result);

      setActionMessage('Credit note approved successfully!');
      setActionMessageType('success');

      if (onApproved) {
        onApproved(result);
      }
    } catch (err) {
      console.error('Error approving credit note:', err);
      const errorMsg =
        err.response?.data?.message || err.message || 'Failed to approve credit note';
      setActionMessage(errorMsg);
      setActionMessageType('error');
    } finally {
      setApproving(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setActionMessage('Please provide a rejection reason');
      setActionMessageType('error');
      return;
    }

    try {
      setActionMessage('');
      setRejecting(true);

      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `/api/credit-notes/${creditNoteId}/reject`,
        { rejectionReason: rejectionReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data.data || response.data;
      setCreditNote(result);

      setActionMessage('Credit note rejected successfully!');
      setActionMessageType('success');
      setShowRejectInput(false);
      setRejectionReason('');

      if (onRejected) {
        onRejected(result);
      }
    } catch (err) {
      console.error('Error rejecting credit note:', err);
      const errorMsg =
        err.response?.data?.message || err.message || 'Failed to reject credit note';
      setActionMessage(errorMsg);
      setActionMessageType('error');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center py-12">
        <div className="text-gray-500">Loading credit note...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!creditNote) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-sm">
          Credit note not found
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(creditNote.status);
  const isApprovable = ['draft', 'pending_approval'].includes(creditNote.status);

  return (
    <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-200">
      {/* Header Row */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {creditNote.creditNoteNumber}
            </h2>
            <span
              className={`inline-flex px-2 py-1 rounded text-xs font-semibold flex-shrink-0 ${statusBadge.bg} ${statusBadge.text}`}
            >
              {statusBadge.label}
            </span>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2 text-sm text-gray-600">
          <span className="font-medium">{creditNote.supplierName}</span>
          <span className="hidden md:inline">•</span>
          <span>
            Dated{' '}
            {new Date(creditNote.creditNoteDate).toLocaleDateString('en-PH', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        </div>
      </div>

      {/* Reference Row */}
      {(creditNote.po || creditNote.supplierInvoice) && (
        <div className="px-6 py-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {creditNote.po && (
              <div>
                <span className="text-gray-600">Linked PO:</span>
                <p className="font-mono font-semibold text-gray-900">
                  {creditNote.po.poNumber || creditNote.po._id}
                </p>
              </div>
            )}
            {creditNote.supplierInvoice && (
              <div>
                <span className="text-gray-600">Supplier Invoice:</span>
                <p className="font-mono font-semibold text-gray-900">
                  {creditNote.supplierInvoice.invoiceNumber ||
                    creditNote.supplierInvoice._id}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reason Badge */}
      <div className="px-6 py-4">
        <span className="inline-block px-3 py-1 bg-amber-100 text-amber-900 rounded text-xs font-semibold">
          {getReasonLabel(creditNote.reason)}
        </span>
      </div>

      {/* Lines Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">
                Product
              </th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700">
                Qty Variance
              </th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700">
                Price Variance
              </th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700">
                Line Amount
              </th>
              {hasVat && (
                <th className="px-4 py-2 text-right font-semibold text-gray-700">
                  VAT
                </th>
              )}
              <th className="px-4 py-2 text-right font-semibold text-gray-700">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {creditNote.lines?.map((line, index) => {
              const lineTotal = Number(line.lineAmount || 0) + Number(line.vatAmount || 0);

              return (
                <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {line.productName}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {Number(line.qtyVariance || 0) !== 0 ? (
                      <span className="text-red-700 font-semibold">
                        +{Math.abs(Number(line.qtyVariance || 0))} units overbilled
                      </span>
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {Number(line.priceVariance || 0) !== 0 ? (
                      <span className="text-red-700 font-semibold">
                        +₱{Math.abs(Number(line.priceVariance || 0)).toFixed(2)} overpriced
                      </span>
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    ₱{Number(line.lineAmount || 0).toFixed(2)}
                  </td>
                  {hasVat && (
                    <td className="px-4 py-3 text-right text-gray-700">
                      ₱{Number(line.vatAmount || 0).toFixed(2)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    ₱{lineTotal.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals Footer */}
      <div className="px-6 py-4 bg-gray-50">
        <div className="flex justify-end">
          <div className="w-full md:w-80 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-medium">₱{Number(creditNote.subtotal || 0).toFixed(2)}</span>
            </div>

            {hasVat && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">VAT:</span>
                <span className="font-medium">
                  ₱{Number(creditNote.vatAmount || 0).toFixed(2)}
                </span>
              </div>
            )}

            <div className="border-t border-gray-300 pt-2 flex justify-between text-base font-semibold">
              <span>Total Credit Amount:</span>
              <span className="text-teal-600">
                ₱{Number(creditNote.totalAmount || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Messages */}
      {actionMessage && (
        <div className="px-6 py-4">
          <div
            className={`px-4 py-3 rounded-md text-sm ${
              actionMessageType === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {actionMessage}
          </div>
        </div>
      )}

      {/* Action Section */}
      {isApprovable && (
        <div className="px-6 py-4">
          {!showRejectInput ? (
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={approving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {approving ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Approving...
                  </>
                ) : (
                  'Approve Credit Note'
                )}
              </button>

              <button
                onClick={() => setShowRejectInput(true)}
                disabled={rejecting}
                className="px-4 py-2 border border-red-600 text-red-600 rounded-md font-medium hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  rows="3"
                  placeholder="Please provide the reason for rejecting this credit note..."
                  disabled={rejecting}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {rejecting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Rejecting...
                    </>
                  ) : (
                    'Confirm Rejection'
                  )}
                </button>

                <button
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectionReason('');
                    setActionMessage('');
                  }}
                  disabled={rejecting}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditNoteApprovalCard;
